import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Minus, Target } from "lucide-react";

const REASON_META = {
  leveling_up: { label: "You're crushing it — try harder", Icon: TrendingUp, tint: "border-brand-emerald/40 bg-brand-emerald/10 text-[#065F46]" },
  easing_off:  { label: "Try easier — build confidence",   Icon: TrendingDown, tint: "border-[#F59E0B] bg-[#FFFBEB] text-[#92400E]" },
  sweet_spot:  { label: "Right on target — keep going",     Icon: Target,     tint: "border-brand-gold/40 bg-brand-blue/10 text-brand-blue" },
  cold_start:  { label: "Start here — a gentle warm-up",    Icon: Minus,      tint: "border-border bg-card text-foreground/80" },
};

const MODULE_ROUTES = {
  dictation: "/dictation",
  legal: "/legal",
  pitman: "/pitman",
  typing: "/typing",
};

const LEVEL_LABELS = ["", "Very Easy", "Easy", "Medium", "Hard", "Very Hard"];

export default function AdaptivePicks({ module, limit = 3, compact = false, title = "Recommended for you" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/recommendations/next`, { params: { module, limit } })
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [module, limit]);

  if (err || !data || !data.candidates?.length) return null;
  const meta = REASON_META[data.reason] || REASON_META.sweet_spot;
  const RIcon = meta.Icon;

  return (
    <section className={compact ? "" : "border border-border p-6 bg-secondary/40"} data-testid={`adaptive-picks-${module}`}>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-1">{title}</div>
          <div className="text-lg sm:text-xl font-black tracking-tight">
            {module.charAt(0).toUpperCase() + module.slice(1)} picks · <span className="text-muted-foreground">Level {data.target_level}</span>
          </div>
        </div>
        <div className={`inline-flex items-center gap-2 px-3 h-8 border ${meta.tint} text-[11px] uppercase tracking-widest font-bold`}>
          <RIcon className="w-3.5 h-3.5" /> {meta.label}
          {data.avg_accuracy != null && <span className="font-mono normal-case tracking-normal ml-1">· {data.avg_accuracy}% avg</span>}
        </div>
      </div>

      <div className={`grid gap-3 ${compact ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
        {data.candidates.map((c) => (
          <Link
            key={c.id}
            to={`${MODULE_ROUTES[module]}/${c.id}`}
            className="border border-border bg-white p-4 hover:border-brand-gold hover:-translate-y-0.5 transition-transform block"
            data-testid={`adaptive-pick-${c.id}`}
          >
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1">
              {LEVEL_LABELS[c.difficulty_score] || "Medium"}
              {c.wpm ? ` · ${c.wpm} WPM` : ""}
              {c.language ? ` · ${c.language.toUpperCase()}` : ""}
              {c.chapter ? ` · ${c.chapter}` : ""}
            </div>
            <div className="text-sm font-black tracking-tight line-clamp-2 min-h-[2.5rem]">{c.title}</div>
            {c.word_count ? (
              <div className="text-[11px] font-mono text-muted-foreground mt-2">{c.word_count} words</div>
            ) : null}
          </Link>
        ))}
      </div>
    </section>
  );
}
